import { TableV3ColDefs } from 'common/table';
import { ICellRendererParams } from 'ag-grid-community';
import { ApplicantsTableColumns } from '../types/ApplicantsTable';
import { Typography, Link, Box } from '@mui/material';
import { formatDateFromNowAbbreviated } from 'utils/datetime';
import { CandidateBatchStatus } from 'services/candidate/types';
import { candidateStatusOrder } from 'modules/analyse/constants/candidateStatusOrder';
import { candidateFilterLabelsByValue } from 'pages/CandidatesRanking/constants/filters';
import { isNumber } from 'utils/validation';
import { roundToTwoDecimals } from 'utils/fomatting';
import { Paths } from 'services/navigation/paths';
import { RouterLink } from 'common/ui/RouterLink';
import {
  CandidateStatus,
  SearchableCandidateSortableFields,
  CandidateOrigin
} from 'generated-graphql/types';
import { StarIcon } from 'common/icons/StarIcon';
import { CandidateStatusChip } from 'modules/analyse/components/CandidateStatusChip';
import { AnchorText } from 'components/Text';
import { IntegrationCredentials } from 'services/integration/types';
import { formatAtsProviderName } from 'core/utils/formatAtsProviderName';
import { IconTooltip } from 'common/ui';
import { ExpandableText } from 'common/ui/ExpandableText';
import { AnalysisErrorChip } from 'services/candidate/components/AnalysisError';

export const applicantsTableColumns = (
  onViewApplicant: (
    row: ApplicantsTableColumns | undefined,
    index: number
  ) => void,
  isAtsApplicationSource: boolean,
  integration: IntegrationCredentials | null
): TableV3ColDefs<
  ApplicantsTableColumns,
  SearchableCandidateSortableFields
> => {
  return [
    {
      field: 'createdAt',
      headerName: 'Applied',
      width: 130,
      sortField: SearchableCandidateSortableFields.CreatedAt,
      cellRenderer: (params: ICellRendererParams<ApplicantsTableColumns>) => {
        if (!params.data) return null;
        return formatDateFromNowAbbreviated(params.data.createdAt);
      }
    },
    {
      field: 'applicantName',
      headerName: 'Applicant',
      flex: 1,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<ApplicantsTableColumns>) => {
        if (!params.data) return null;
        return (
          <Box display="flex" width="100%" gap="10px" alignItems="center">
            <Link
              component="button"
              onClick={() =>
                onViewApplicant(params.data, params.node?.rowIndex ?? 0)
              }
              sx={{
                textTransform: 'capitalize'
              }}
            >
              <AnchorText>{params.value || ''}</AnchorText>
            </Link>
            {params.data.isAllRequirementsMet && (
              <IconTooltip title="Met knockout requirements">
                <span style={{ display: 'inline-flex' }}>
                  <StarIcon width={20} height={20} />
                </span>
              </IconTooltip>
            )}
          </Box>
        );
      }
    },
    {
      field: 'jobTitle',
      headerName: 'Job title',
      flex: 1,
      cellRenderer: (params: ICellRendererParams<ApplicantsTableColumns>) => {
        if (!params.data) return '-';
        const jobTitle = params.value;

        if (
          params.data.candidateBatchStatus === CandidateBatchStatus.ARCHIVED
        ) {
          return <Typography>{jobTitle} - archived</Typography>;
        }

        return (
          <RouterLink to={`${Paths.ANALYSE}/${params.data.candidateBatchId}`}>
            {jobTitle}
          </RouterLink>
        );
      }
    },
    {
      field: 'jobReference',
      headerName: 'Job ref',
      flex: 0.5,
      cellRenderer: (params: ICellRendererParams<ApplicantsTableColumns>) => {
        return <Typography variant="body2">{params.value}</Typography>;
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      cellStyle: {
        display: 'flex',
        alignItems: 'flex-start'
      },
      cellRenderer: (params: ICellRendererParams<ApplicantsTableColumns>) => {
        return <CandidateStatusChip status={params.value as CandidateStatus} />;
      },
      filterOptions: {
        items: (Object.values(CandidateStatus) as Array<CandidateStatus>)
          .map((value) => ({
            value: value,
            label: candidateFilterLabelsByValue[value] || value
          }))
          .sort((a, b) => {
            return (
              (candidateStatusOrder[a.value as CandidateStatus] || -1) -
              (candidateStatusOrder[b.value as CandidateStatus] || -1)
            );
          }),
        selectionType: 'multiple'
      }
    },
    {
      field: 'score',
      headerName: 'Score',
      flex: 0.5,
      cellRenderer: (params: ICellRendererParams<ApplicantsTableColumns>) => {
        if (params.data?.analysisErrorCode) {
          return <AnalysisErrorChip code={params.data.analysisErrorCode} />;
        }

        return (
          <Typography variant="body2">
            {isNumber(params.value)
              ? `${roundToTwoDecimals((params.value ?? 0) * 100)}%`
              : '-'}
          </Typography>
        );
      },
      sortField: SearchableCandidateSortableFields.TotalScoreScaled
    },
    {
      field: 'atsApplicationSource',
      headerName: `${formatAtsProviderName(integration?.provider)} source`,
      flex: 1,
      hide: !isAtsApplicationSource,
      cellRenderer: (params: ICellRendererParams<ApplicantsTableColumns>) => {
        if (!params.data) return null;
        const value = params.value as string | null | undefined;
        if (!value) {
          return <Typography variant="body2">-</Typography>;
        }
        return <ExpandableText text={value} characterLimit={40} />;
      },
      searchable: true
    },
    {
      field: 'origin',
      headerName: 'Origin',
      width: 130,
      cellRenderer: (params: ICellRendererParams<ApplicantsTableColumns>) => {
        const getOriginLabel = (origin: string | null): string => {
          if (!origin) return '-';

          switch (origin) {
            case CandidateOrigin.ExternallySourcedViaContactOut:
              return 'Popp external';
            case CandidateOrigin.InternallySourcedViaAts:
              return 'Popp internal';
            case CandidateOrigin.NonAtsCvUpload:
              return 'Uploaded manually';
            case CandidateOrigin.NonAtsTypeform:
              return 'Application link';
            default:
              return '-';
          }
        };

        return (
          <Typography variant="body2">
            {getOriginLabel(params.value)}
          </Typography>
        );
      },
      filterOptions: {
        items: [
          {
            value: CandidateOrigin.ExternallySourcedViaContactOut,
            label: 'Popp external'
          },
          {
            value: CandidateOrigin.InternallySourcedViaAts,
            label: 'Popp internal'
          },
          {
            value: CandidateOrigin.NonAtsCvUpload,
            label: 'Uploaded manually'
          },
          {
            value: CandidateOrigin.NonAtsTypeform,
            label: 'Application link'
          }
        ],
        selectionType: 'multiple'
      }
    }
  ];
};
